import React, { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { applyTheme, readChoice, type ThemeChoice } from '../lib/theme';
import { api, getToken } from '../lib/api';

const OPTIONS: { value: ThemeChoice; icon: React.ReactNode; label: string }[] = [
  { value: 'light', icon: <Sun className="w-4 h-4" />, label: 'Claro' },
  { value: 'dark', icon: <Moon className="w-4 h-4" />, label: 'Oscuro' },
];

/**
 * Selector de tema: claro u oscuro, nada más.
 *
 * En la primera visita arranca en el tema del dispositivo (ver `lib/theme`),
 * así que no hace falta una opción "Sistema" para respetarlo: ya se respeta
 * hasta que alguien decide otra cosa.
 */
export const ThemeToggle: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const [choice, setChoice] = useState<ThemeChoice>(() => readChoice());

  useEffect(() => {
    applyTheme(choice);
  }, [choice]);

  /**
   * Cambia el tema y, si hay sesión, lo guarda en la cuenta.
   *
   * Se sigue escribiendo en el navegador además del servidor: es lo que evita
   * el parpadeo al cargar, porque el script de `index.html` lo lee antes de que
   * exista React, y es lo único que tiene quien no ha entrado.
   */
  const elegir = (valor: ThemeChoice) => {
    setChoice(valor);
    if (getToken()) {
      // Sin await ni aviso de error: es una preferencia, no vale interrumpir
      // a nadie porque no haya podido guardarse en el servidor.
      void api.patch('/profile', { theme: valor }).catch(() => {});
    }
  };

  if (compact) {
    // En espacios estrechos, un solo botón que alterna a partir de lo que se ve.
    return (
      <button
        onClick={() => elegir(choice === 'light' ? 'dark' : 'light')}
        className="p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
        aria-label={choice === 'light' ? 'Cambiar a tema oscuro' : 'Cambiar a tema claro'}
        title={choice === 'light' ? 'Tema oscuro' : 'Tema claro'}
      >
        {choice === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Tema de la interfaz"
      className="inline-flex items-center gap-0.5 p-1 rounded-lg bg-ink-850 border border-ink-700 w-full"
    >
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          role="radio"
          aria-checked={choice === option.value}
          onClick={() => elegir(option.value)}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
            choice === option.value ? 'bg-ink-700 text-ink-50' : 'text-ink-400 hover:text-ink-100'
          }`}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
};
